import { ArrowTopRightIcon, InstagramLogoIcon } from '@radix-ui/react-icons';
import { Skeleton } from '@radix-ui/themes';
import { Link, useNavigate } from 'react-router-dom';
import { isCrawler, isCurrentUserAdmin, isCurrentUserPremium } from 'helpers/authentication';
import { getRegionCode, italianProvincesNames } from 'helpers/playerData';
import { useWindowSize } from 'helpers/useWindowSize';
import { openInExternalUrl } from 'helpers/utils';
import type { ClubDetailType } from 'types/club';
import type { Category, MaxRankType } from 'types/player';
import type { ProfilesAvailableType } from 'types/user';
import AdKaoraBanner, { AdSlot } from './AdKaoraBanner';
import AgeCategoryLabel from './AgeCategoryLabel';
import Container from './Container';
import { FacebookIcon } from './SocialIcons';

const PROFILES: { [key: string]: string } = {
  talker_profile: 'Talker',
  player_profile: 'Giocatore FITP',
  teacher: 'Maestro di Tennis',
  trainer: 'Preparatore atletico',
  mental_coach: 'Mental Coach',
  nutritionist: 'Nutrizionista',
  shop: 'Shop',
};

const PROFILES_MAP: { [key: string]: string } = {
  talker_profile: 'talker',
  player_profile: 'fit_player_profile',
  teacher: 'teacher_profile',
  trainer: 'trainer_profile',
  mental_coach: 'mental_coach_profile',
  nutritionist: 'nutritionist_profile',
  shop: 'shop_profile',
};

export const PlayerBasicInfo: React.FC<{
  profiles?: ProfilesAvailableType;
  user_id?: number;
  fit_player_profile_id?: number;
  max_rank?: MaxRankType;
  club?: ClubDetailType;
  category?: Category;
  fb_id?: string;
  ig_id?: string;
  loading: boolean;
  userProfile: boolean;
  anagrafica_code?: string;
  card_number?: string;
  age?: number;
  email?: string;
}> = ({
  profiles,
  user_id,
  fit_player_profile_id,
  max_rank,
  club,
  category,
  fb_id,
  ig_id,
  userProfile,
  loading,
  card_number,
  anagrafica_code,
  age,
  email,
}) => {
  const isUser = user_id && user_id !== fit_player_profile_id;
  profiles = Object.assign({}, { talker_profile: isUser ? true : false, player_profile: !userProfile }, profiles);
  const size = useWindowSize();
  const isDesktop = size.width > 1024;
  const navigate = useNavigate();

  const clickProfile = (profile: string) => {
    navigate({
      pathname: '/cerca',
      search: `?page=other_profiles&profile=${PROFILES_MAP[profile]}`,
    });
  };

  return (
    <Container title='Informazioni base:'>
      <div id='info' className='flex w-full flex-row flex-wrap items-stretch gap-2 self-stretch'>
        {!isCrawler() && !isCurrentUserPremium() && (
          <>
            <div className='clever-core-ads' />
            <div className='clever-core-ads-offerwall' />
          </>
        )}

        {!userProfile && (
          <div
            id='maxrank'
            className='border-neutral-neutral-2 flex grow flex-col items-start self-auto rounded-xl border border-solid bg-white p-3'
          >
            <div id='title5' className='text-neutral-neutral-8 text-sm font-medium tracking-normal'>
              Massima classifica raggiunta
            </div>
            <div id='value5' className='text-neutral-neutral-alpha-12 tracking-normal'>
              <Skeleton loading={loading} width='80px' height='24px'>
                <span className='text-lg font-bold'>{max_rank?.rank}</span> nel {max_rank?.year}
              </Skeleton>
            </div>
            <div className='text-accent-accent-9 mt-3 cursor-pointer text-sm font-bold'>
              <Link to={`/giocatore/${fit_player_profile_id}/statistiche`}>
                Scopri l'evoluzione della classifica del giocatore negli anni
                <ArrowTopRightIcon />
              </Link>
            </div>
          </div>
        )}
        <AdKaoraBanner slot={AdSlot.SPALLA_TOP} />
        {!userProfile && club !== null && (
          <>
            <div
              id='circolo'
              className='border-neutral-neutral-2 flex grow flex-col items-start self-auto rounded-xl border border-solid bg-white p-3'
            >
              <div id='title4' className='text-neutral-neutral-8 text-sm font-medium tracking-normal'>
                Circolo
              </div>
              <div id='value4' className='text-neutral-neutral-alpha-12 text-lg font-bold tracking-normal'>
                <Skeleton loading={loading}>
                  <Link className='min-h-6 min-w-20 underline' to={`/circolo/${club?.id}`}>
                    {club?.name}
                  </Link>
                </Skeleton>
              </div>
            </div>

            <div
              id='Provincia'
              className='border-neutral-neutral-2 flex grow flex-col items-start self-auto rounded-xl border border-solid bg-white p-3'
            >
              <div id='title3' className='text-neutral-neutral-8 text-sm font-medium tracking-normal'>
                Provincia
              </div>
              <Skeleton loading={loading}>
                <div
                  id='value3'
                  className='text-neutral-neutral-alpha-12 min-h-6 min-w-20 text-lg font-bold tracking-normal'
                >
                  <Link
                    className='pr-1'
                    to={{
                      pathname: '/classifiche', // Base path
                      search: `?region=${club?.region && (club.region.length > 3 ? getRegionCode(club.region) : club.region)}`,
                    }}
                  >
                    <span className='underline'>{club?.region}</span>
                  </Link>
                  ,
                  <Link
                    className='pl-1'
                    to={{
                      pathname: '/classifiche',
                      search: `?province=${club?.province}`,
                    }}
                  >
                    <span className='underline'>{club?.province}</span>
                  </Link>
                  <div className='text-accent-accent-9 mt-3 cursor-pointer text-sm font-bold'>
                    <Link to={`/classifiche?province=${club?.province}`}>
                      Scopri i giocatori più forti della provincia di {club ? italianProvincesNames[club.province] : ''}
                      <ArrowTopRightIcon />
                    </Link>
                  </div>
                </div>
              </Skeleton>
            </div>
          </>
        )}

        {!userProfile && (
          <div
            id='Categoria'
            className='border-neutral-neutral-2 flex grow flex-col items-start self-auto rounded-xl border border-solid bg-white p-3'
          >
            <div id='title2' className='text-neutral-neutral-8 text-sm font-medium tracking-normal'>
              Categoria età
            </div>
            <div id='value2' className='s text-neutral-neutral-alpha-12 tracking-normal'>
              <AgeCategoryLabel loading={loading} ageCategory={category} linkTo={true} className='min-w-20 text-lg' />
            </div>
            <div className='text-accent-accent-9 mt-3 cursor-pointer text-sm font-bold'>
              <Link to={`/classifiche?category=${category}`}>
                Scopri i giocatori più forti d'Italia {category} <ArrowTopRightIcon />
              </Link>
            </div>
          </div>
        )}

        <div
          id='tipo'
          className='border-neutral-neutral-2 flex min-h-20 grow flex-col items-start self-auto rounded-xl border border-solid bg-white p-3'
        >
          <div id='title1' className='text-neutral-neutral-8 text-sm font-medium tracking-normal'>
            Tipo utente
          </div>
          <Skeleton loading={loading}>
            <div id='value1' className='text-neutral-neutral-alpha-12 text-lg font-bold tracking-normal'>
              {Object.keys(profiles)
                .filter((profile) => profiles[profile as keyof ProfilesAvailableType] === true)
                .map((profile: string, index: number, filteredProfiles: string[]) => (
                  <div
                    key={`${profile}-profile`}
                    className='cursor-pointer underline'
                    onClick={() => clickProfile(profile)}
                  >
                    {PROFILES[profile]}
                    {index < filteredProfiles.length - 1 && ' / '}
                  </div>
                ))}
            </div>
          </Skeleton>
        </div>

        {(fb_id || ig_id) && (
          <div
            id='socials'
            className='border-neutral-neutral-2 flex grow flex-col items-start self-auto rounded-xl border border-solid bg-white p-3'
          >
            <div id='title5' className='text-neutral-neutral-8 text-sm font-medium tracking-normal'>
              Socials
            </div>
            <div
              id='value5'
              className='text-neutral-neutral-alpha-12 flex cursor-pointer flex-col gap-5 text-lg font-bold tracking-normal'
            >
              {ig_id && (
                <div
                  onClick={() => openInExternalUrl(`https://instagram.com/${ig_id}`)}
                  className='flex items-center space-x-3'
                >
                  <div className='text-gray-9 w-8'>
                    <InstagramLogoIcon className='h-6 w-6 pr-1' />
                  </div>
                  {ig_id} <ArrowTopRightIcon />
                </div>
              )}
              {fb_id && (
                <div
                  onClick={() => openInExternalUrl(`https://facebook.com/${fb_id}`)}
                  className='flex items-center space-x-3'
                >
                  <div className='text-gray-9 w-8'>
                    <FacebookIcon />
                  </div>
                  {fb_id}
                  <ArrowTopRightIcon />
                </div>
              )}
            </div>
          </div>
        )}
        {isCurrentUserAdmin() && !userProfile && (
          <>
            <div
              id='fitcard'
              className='border-neutral-neutral-2 bg-reda-4 flex grow flex-col items-start self-auto rounded-xl border border-solid p-3'
            >
              <div className='text-neutral-neutral-8 text-sm font-medium tracking-normal'>Tessera</div>
              <div className='text-neutral-neutral-alpha-12 tracking-normal'>
                <Skeleton loading={loading} width='80px' height='24px'>
                  <span className='text-lg font-bold'>{card_number}</span>
                </Skeleton>
              </div>
            </div>
            <div
              id='anagrafica'
              className='border-neutral-neutral-2 bg-reda-4 flex grow flex-col items-start self-auto rounded-xl border border-solid p-3'
            >
              <div className='text-neutral-neutral-8 text-sm font-medium tracking-normal'>Codice anagrafica</div>
              <div className='text-neutral-neutral-alpha-12 tracking-normal'>
                <Skeleton loading={loading} width='80px' height='24px'>
                  <span className='text-lg font-bold'>{anagrafica_code}</span>
                </Skeleton>
              </div>
            </div>
            <div
              id='age'
              className='border-neutral-neutral-2 bg-reda-4 flex grow flex-col items-start self-auto rounded-xl border border-solid p-3'
            >
              <div className='text-neutral-neutral-8 text-sm font-medium tracking-normal'>Età</div>
              <div className='text-neutral-neutral-alpha-12 tracking-normal'>
                <Skeleton loading={loading} width='80px' height='24px'>
                  <span className='text-lg font-bold'>{age} anni</span>
                </Skeleton>
              </div>
            </div>
            {email && (
              <div
                id='mail'
                className='border-neutral-neutral-2 bg-reda-4 flex grow flex-col items-start self-auto rounded-xl border border-solid p-3'
              >
                <div className='text-neutral-neutral-8 text-sm font-medium tracking-normal'>Mail</div>
                <div className='text-neutral-neutral-alpha-12 tracking-normal'>
                  <Skeleton loading={loading} width='80px' height='24px'>
                    <span className='text-lg font-bold'>{email}</span>
                  </Skeleton>
                </div>
              </div>
            )}
          </>
        )}
        {isDesktop && <AdKaoraBanner slot={AdSlot.SPALLA_MIDDLE} />}
      </div>
    </Container>
  );
};

export default PlayerBasicInfo;
